import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, CircleAlert, CircleCheck } from "lucide-react";

export default function StripeSettings() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ publishable_key: "", secret_key: "", mode: "live" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/stripe/keys").then(r => setStatus(r.data)).catch(e => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.publishable_key.startsWith("pk_") || !form.secret_key.startsWith("sk_")) {
      toast.error("Claves inválidas. Deben empezar por pk_ y sk_"); return;
    }
    setSaving(true);
    try {
      await api.post("/admin/stripe/keys", form);
      toast.success(`Stripe ${form.mode.toUpperCase()} configurado. Los cobros ya van a tu cuenta.`);
      setForm({ publishable_key: "", secret_key: "", mode: "live" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="hub-card p-6">
        <div className="flex items-center gap-3 mb-3">
          <CreditCard className="hub-coral"/>
          <h3 className="font-serif text-2xl">Estado actual</h3>
        </div>
        {status?.configured ? (
          <div className="flex items-center gap-2">
            <CircleCheck className="text-emerald-500"/>
            <div>
              <div className="font-display font-semibold">Stripe {status.mode.toUpperCase()} conectado</div>
              <div className="text-xs text-[#5C6B7D]">Clave termina en …{status.publishable_key_last4} · actualizada {status.updated_at ? new Date(status.updated_at).toLocaleString("es-ES") : "—"}</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <CircleAlert className="text-amber-500"/>
            <div>
              <div className="font-display font-semibold">Modo demo (sandbox)</div>
              <div className="text-xs text-[#5C6B7D]">Los pagos son de prueba. Conecta tus claves para cobrar de verdad.</div>
            </div>
          </div>
        )}
      </div>

      <div className="hub-card p-6">
        <h3 className="font-serif text-2xl">Conectar mi cuenta Stripe</h3>
        <p className="text-sm text-[#5C6B7D] mt-1">Pega aquí las 2 claves de tu cuenta Stripe. Se guardan cifradas y solo se usan en el servidor.</p>

        <ol className="mt-4 space-y-2 text-sm text-[#0B1D3A]">
          <li>1. Abre <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="hub-coral font-semibold" data-testid="stripe-open-keys">dashboard.stripe.com/apikeys</a></li>
          <li>2. Copia la <strong>Publishable key</strong> (empieza por <code>pk_live_</code>)</li>
          <li>3. Pulsa "Reveal live key" y copia la <strong>Secret key</strong> (empieza por <code>sk_live_</code>)</li>
          <li>4. Pégalas aquí abajo y guarda</li>
        </ol>

        <div className="mt-6 space-y-3">
          <div className="flex gap-2 p-1 bg-[#FBF9F5] border border-[#0B1D3A]/10 rounded-full w-fit">
            {["live", "test"].map(m => (
              <button key={m} onClick={()=>setForm({...form, mode: m})} data-testid={`stripe-mode-${m}`}
                className={`text-xs px-4 py-1.5 rounded-full font-display font-semibold ${form.mode === m ? "bg-[#0B1D3A] text-white" : "text-[#0B1D3A]"}`}>
                {m === "live" ? "LIVE (cobras de verdad)" : "TEST (pruebas)"}
              </button>
            ))}
          </div>
          <div>
            <Label>Publishable key (pk_...)</Label>
            <Input value={form.publishable_key} onChange={e=>setForm({...form, publishable_key: e.target.value.trim()})} placeholder="pk_live_51ABC..." className="font-mono text-xs" data-testid="stripe-pk-input"/>
          </div>
          <div>
            <Label>Secret key (sk_...)</Label>
            <Input type="password" value={form.secret_key} onChange={e=>setForm({...form, secret_key: e.target.value.trim()})} placeholder="sk_live_51ABC..." className="font-mono text-xs" data-testid="stripe-sk-input"/>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-xs text-emerald-900">
            <ShieldCheck size={16} className="shrink-0 mt-0.5"/>
            <div>Las claves se cifran y guardan solo en el servidor. Nunca las mostramos al navegador. Tu Publishable key es pública (aparece en los checkouts), la Secret key nunca sale del backend.</div>
          </div>
          <Button onClick={save} disabled={saving} className="hub-btn-primary w-full gap-2" data-testid="stripe-save-btn">
            <CreditCard size={14}/> {saving ? "Verificando con Stripe..." : "Guardar y activar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
